import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';

import Badge from '../Badge/Badge';
import { Typography } from '../Typography/Typography';
import type { ReducedPriceProps } from './ReducedPrice.entities';

import styles from './ReducedPrice.module.css';

const ReducedPrice = ({
  locale,
  pricing,
  variant = 'default',
  strings,
  hasCampaignDiscount,
  shallShowVatText = false,
  vatAdded = false,
}: ReducedPriceProps) => {
  const { discount, originalPrice, price, vatRate, isVatIncluded } = pricing;
  const isUk = locale === 'uk';

  if (!discount || !originalPrice) return null;

  const DiscountDetails = () => (
    <div className={styles.originalPrice}>
      <Typography variant="body2" className={styles.strikedPrice} data-variant={variant}>
        {getFormattedCurrency(originalPrice, locale)}
      </Typography>
      {!hasCampaignDiscount && (
        <Typography variant="subheading2" className={styles.discountPrice} data-variant={variant}>
          {getFormattedCurrency(discount, locale)} {isUk && strings.off}
        </Typography>
      )}
      {hasCampaignDiscount && (
        <Badge color="mint" dataTestId="vehicle-tile-badge-heycar-days" inline>
          {getFormattedCurrency(discount, locale)} {isUk && strings.off}
        </Badge>
      )}
    </div>
  );

  const VatText = () =>
    shallShowVatText && (
      <Typography variant="caption2" Component="p" className={styles.vat}>
        {strings.vat}
      </Typography>
    );

  const DiscountedPrice = () => (
    <div className={styles.discountPriceContainer} data-variant={variant}>
      {vatAdded ? (
        <Typography
          Component="span"
          variant={variant === ('carWithFinance' || 'podium') ? 'body1' : 'h3'}
          className={`${styles.price} ${styles.vatAdded}`}
          data-variant={variant}
        >
          {(variant === 'carTileWithFinance' || 'carWithFinance') && ` ${strings.or} `}
          {getFormattedCurrency(price, locale)}
          {vatAdded && <Typography variant="caption2">{strings.vatIncluded}</Typography>}
        </Typography>
      ) : (
        <>
          <Typography
            Component="span"
            variant={variant === ('carWithFinance' || 'podium') ? 'body1' : 'h3'}
            className={styles.price}
            data-variant={variant}
          >
            {(variant === 'carTileWithFinance' || variant === 'carWithFinance') &&
              ` ${strings.or} `}
            {getFormattedCurrency(price, locale)}
            {isVatIncluded && vatRate}
          </Typography>
          {VatText()}
        </>
      )}
    </div>
  );

  const order =
    variant === 'carTileWithFinance' || variant === 'carWithFinance'
      ? [DiscountedPrice, DiscountDetails]
      : [DiscountDetails, DiscountedPrice];

  return (
    <div className={styles.discountPriceWrapper} data-variant={variant}>
      {order.map((Component, key) => (
        <Component key={key} />
      ))}
    </div>
  );
};

export { ReducedPrice };
